import React from 'react';

export interface PowerRingProps {
  /** Diámetro exterior del anillo (= tamaño del botón). */
  size: number;
  /** Grosor; crece hacia adentro (misma geometría que border-box CSS). */
  borderWidth: number;
  color: string;
  /**
   * Progreso del timer: 0 = anillo completo, 1 = vacío.
   * Si se omite, anillo estático completo.
   */
  progress?: number;
  /**
   * Sentido del vaciado del timer.
   * - cw: sentido actual (horario desde las 12) — auto-on
   * - ccw: sentido contrario — auto-off
   */
  direction?: 'cw' | 'ccw';
}

/**
 * Anillo del PowerButton dibujado con SVG stroke (anti-alias nítido).
 * Misma geometría que un border CSS inset + border-box: Ø exterior = size,
 * el grosor solo come hacia el centro.
 */
const PowerRing: React.FC<PowerRingProps> = ({
  size,
  borderWidth,
  color,
  progress,
  direction = 'cw',
}) => {
  if (size <= 0 || borderWidth <= 0) return null;
  if (progress !== undefined && progress >= 1) return null;

  const cx = size / 2;
  const cy = size / 2;
  // Centro del stroke: borde exterior toca el borde del botón.
  const r = Math.max(0.5, (size - borderWidth) / 2);
  const isTimed = progress !== undefined;
  const p = isTimed ? Math.min(Math.max(progress, 0), 1) : 0;
  const circumference = 2 * Math.PI * r;
  const reverse = isTimed && direction === 'ccw';

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'visible',
        display: 'block',
        // Espejo horizontal: invierte el sentido del vaciado (auto-off).
        transform: reverse ? 'scaleX(-1)' : undefined,
      }}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={borderWidth}
        // Vacía en horario desde las 12 (igual que el conic-gradient anterior).
        transform={isTimed ? `rotate(-90 ${cx} ${cy})` : undefined}
        strokeDasharray={isTimed ? circumference : undefined}
        strokeDashoffset={isTimed ? circumference * p : undefined}
        style={{
          transition: isTimed ? undefined : 'stroke 0.3s ease-in-out',
        }}
      />
    </svg>
  );
};

export default React.memo(PowerRing);
